// Mirror of Shared/Calculators/RoofingCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "footprintLengthFt", kind: "number", default: 40, min: 5, max: 300 },
  { key: "footprintWidthFt", kind: "number", default: 30, min: 5, max: 300 },
  {
    key: "pitch",
    kind: "select",
    defaultChoice: "4-12",
    choices: [
      { value: "3-12", set: { pitchMultiplier: 1.0308 } },
      { value: "4-12", set: { pitchMultiplier: 1.0541 } },
      { value: "6-12", set: { pitchMultiplier: 1.118 } },
      { value: "8-12", set: { pitchMultiplier: 1.2019 } },
      { value: "12-12", set: { pitchMultiplier: 1.4142 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 10, min: 0, max: 25 },
  { key: "pricePerBundle", kind: "number", default: 35, min: 0, max: 200 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const l = F.get(i, "footprintLengthFt");
  const w = F.get(i, "footprintWidthFt");
  const mult = F.get(i, "pitchMultiplier");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerBundle");

  const footprint = l * w;
  const roofArea = footprint * mult;
  const areaWithWaste = roofArea * (1 + waste / 100);
  const squares = areaWithWaste / 100;
  const bundles = F.ceilUnits(squares * 3);
  const underlaymentRolls = F.ceilUnits(squares / 2);
  const cost = bundles * price;

  return {
    footprint: F.round(footprint, 2),
    roofArea: F.round(roofArea, 2),
    areaWithWaste: F.round(areaWithWaste, 2),
    squares: F.round(squares, 2),
    bundles,
    underlaymentRolls,
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const bundles = F.get(r, "bundles");
  return [
    { label: "Roof area", value: `${F.dec(F.get(r, "roofArea"))} sq ft` },
    { label: "Roofing squares", value: F.dec(F.get(r, "squares")) },
    { label: "Shingle bundles", value: `${F.intStr(bundles)} ${F.plural(bundles, "bundle", "bundles")}`, highlight: true },
    { label: "Underlayment rolls", value: F.intStr(F.get(r, "underlaymentRolls")) },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — field shingles only, excludes starter, ridge & labor" },
  ];
}

export const roofing: CalcDef = { slug: "roofing-calculator", fields, compute, present };
